import { useEffect, useState } from 'preact/hooks';
import { type Repository } from '../api/code-forge/interface';
import { FlashMessage, flash } from '../components/flash-message';
import { useApi } from '../hooks/api';
import { useConfigStore } from '../stores/config';

export const SettingsPage = () => {
    const [githubToken, diffView, pushRepository, pushBranch, commitDirectly] = useConfigStore((s) => [
        s.githubToken,
        s.diffView,
        s.pushRepository,
        s.pushBranch,
        s.commitDirectly,
    ]);
    const [setGithubToken, setDiffView, setPushRepository, setPushBranch, setCommitDirectly] = useConfigStore((s) => [
        s.setGithubToken,
        s.setDiffView,
        s.setPushRepository,
        s.setPushBranch,
        s.setCommitDirectly,
    ]);

    const api = useApi();
    const [userRepositories, setUserRepositories] = useState<Repository[]>();
    const [canCommitDirectly, setCanCommitDirectly] = useState(false);

    useEffect(() => {
        document.title = 'Settings · Datenanfragen.de data editor';
    }, []);

    useEffect(() => {
        if (!api) return;
        (async () => {
            try {
                setUserRepositories(await api.listRepositoriesWithPushAccess());
                setCanCommitDirectly(await api.canPushToRepository(api.repository));
            } catch (err) {
                flash(
                    <FlashMessage type="error">
                        Couldn't fetch user data from GitHub: {(err as Error).message}
                    </FlashMessage>
                );
            }
        })();
    }, [api]);

    return (
        <>
            <h2>Settings</h2>

            <form>
                <div className="row mb-3">
                    <label htmlFor="input-setting-github-token" className="col-sm-2 col-form-label">
                        GitHub token
                    </label>
                    <div className="col-sm-10">
                        <input
                            type="text"
                            className="form-control"
                            id="input-setting-github-token"
                            value={githubToken}
                            onInput={(e) => setGithubToken(e.currentTarget.value)}
                        />
                        <div className="form-text">
                            You need to specify a{' '}
                            <a href="https://github.com/settings/tokens/new?scopes=repo">personal access token</a> for
                            data editor to be able to act on your behalf on GitHub. The token needs to have the{' '}
                            <code>repo</code> scope.
                            <br />
                            <a href="https://github.com/datenanfragen/data-editor/issues/5">
                                Support for fine-grained access tokens
                            </a>{' '}
                            is planned but doesn’t work yet.
                        </div>
                    </div>
                </div>

                <div className="row mb-3">
                    <label htmlFor="input-setting-diff-view" className="col-sm-2 col-form-label">
                        Diff view
                    </label>
                    <div className="col-sm-10">
                        <select
                            id="input-setting-diff-view"
                            className="form-select"
                            value={diffView}
                            onChange={(e) => setDiffView(e.currentTarget.value as 'split')}>
                            <option value="unified">unified</option>
                            <option value="split">split</option>
                        </select>
                    </div>
                </div>

                <div className="row mb-3">
                    <label htmlFor="input-setting-push-repository" className="col-sm-2 col-form-label">
                        Repository for pushing
                    </label>
                    <div className="col-sm-10">
                        <select
                            id="input-setting-push-repository"
                            className="form-select"
                            value={pushRepository ? `${pushRepository.owner}/${pushRepository.repo}` : '__none__'}
                            onChange={(e) => {
                                if (e.currentTarget.value === '__none__') {
                                    setPushRepository(undefined);
                                    return;
                                }
                                const [owner, repo] = e.currentTarget.value.split('/');
                                if (owner && repo) setPushRepository({ owner, repo });
                            }}
                            disabled={!userRepositories}>
                            <option value="__none__">
                                {userRepositories ? 'Choose a repository' : 'Loading repositories…'}
                            </option>
                            {userRepositories?.map((r) => (
                                <option value={`${r.owner}/${r.repo}`} key={`${r.owner}/${r.repo}`}>
                                    {r.owner}/{r.repo}
                                </option>
                            ))}
                        </select>
                        <div className="form-text">
                            The repository to push your changes to (your fork of the data repository). If you don't have
                            a fork yet, you can{' '}
                            <a href="https://github.com/datenanfragen/data/fork" target="_blank" rel="noreferrer">
                                create one here
                            </a>
                            . You will need to reload this page after creating the fork for it to show up here.
                        </div>
                    </div>
                </div>

                {pushRepository && (
                    <div className="row mb-3">
                        <label htmlFor="input-setting-push-branch" className="col-sm-2 col-form-label">
                            Branch for pushing
                        </label>
                        <div className="col-sm-10">
                            <input
                                type="text"
                                className="form-control"
                                id="input-setting-push-branch"
                                value={pushBranch}
                                onInput={(e) => setPushBranch(e.currentTarget.value)}
                            />
                            <div className="form-text">The branch in your repository to create new branches from.</div>
                        </div>
                    </div>
                )}

                {canCommitDirectly && (
                    <div className="row mb-3">
                        <div className="col-sm-10 offset-sm-2">
                            <div className="form-check">
                                <input
                                    className="form-check-input"
                                    type="checkbox"
                                    id="input-setting-commit-directly"
                                    checked={commitDirectly}
                                    onChange={(e) => setCommitDirectly(e.currentTarget.checked)}
                                />
                                <label className="form-check-label" htmlFor="input-setting-commit-directly">
                                    Commit changes directly to the default branch
                                </label>
                                <div className="form-text">
                                    As you have write access to the data repository, you can choose to commit your
                                    changes directly to the default branch, skipping the pull request process.
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </form>
        </>
    );
};
